import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useSolicitacaoAvaliacao } from '@/hooks/useSolicitacaoAvaliacao';

interface SolicitacaoAvaliacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  cliente: {
    id: string;
    razao_social: string;
    cnpj: string;
  };
  solicitanteId: string;
  origem?: string;
  onSuccess?: (solicitacaoId: string) => void;
}

export function SolicitacaoAvaliacaoModal({
  isOpen,
  onClose,
  cliente,
  solicitanteId,
  origem,
  onSuccess,
}: SolicitacaoAvaliacaoModalProps) {
  const [justificativa, setJustificativa] = useState('');
  const { createSolicitacao, isCreating } = useSolicitacaoAvaliacao();

  const handleSubmit = async () => {
    if (justificativa.trim().length < 10) {
      return;
    }

    try {
      const sol = await createSolicitacao(cliente.id, solicitanteId, justificativa.trim(), origem);
      setJustificativa('');
      onClose();
      if (sol) onSuccess?.(sol.id);
    } catch { /* O hook mostra o erro; preserve a justificativa para tentar novamente. */ }
  };

  const handleClose = () => {
    if (isCreating) return;
    setJustificativa('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Solicitar transferência de carteira</DialogTitle>
          <DialogDescription>
            Dono carteira: o master decide. Dono negócio: a gerência decide. Acompanhe a resposta na Busca 360, em Solicitações e aprovações.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Informações do Cliente */}
          <div className="bg-muted/30 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Cliente:</Label>
              <Badge variant="outline">{cliente.cnpj}</Badge>
            </div>
            <p className="text-sm font-medium">{cliente.razao_social}</p>
          </div>

          {/* Campo de Justificativa */}
          <div className="space-y-2">
            <Label htmlFor="justificativa">
              Explique por que você deve atender este cliente:
            </Label>
            <Textarea
              id="justificativa"
              placeholder="Ex: Cliente solicitou diretamente meu atendimento devido ao relacionamento anterior..."
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {justificativa.length}/500 caracteres · mínimo de 10
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={justificativa.trim().length < 10 || isCreating}
          >
            {isCreating ? 'Enviando...' : 'Enviar Solicitação'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}